/**
 * The personal group leaderboard (GroupLeaderboard.tsx), ranked by reward
 * points. Pure — the Firestore reads live in the component.
 *
 * Members come from getGroupMembers (src/lib/firebase/queries/group-members.ts),
 * which returns one row per member as `{ id: <users doc id>, ...userData, role }`.
 * The users doc's own `id` field is the member's uid (getGroupMembers matches
 * on it) and the spread lets it win over the doc id, so `id` IS the uid that
 * keys `customerPoints`. There is no `userId` on these rows; reading one made
 * every member fail and the leaderboard render empty.
 */
export interface LeaderboardSourceMember {
  id?: string;
  userId?: string;
  displayName?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  firstNameEn?: string;
  lastNameEn?: string;
  firstNameAr?: string;
  lastNameAr?: string;
  email?: string;
  photoURL?: string;
}

export interface LeaderboardMember {
  id: string;
  name: string;
  nameEn: string;
  nameAr: string;
  photoURL?: string;
  rewardPoints: number;
  rank: number;
}

/** The member's uid: `id` as getGroupMembers returns it, `userId` for a raw groupMembers row. */
export function leaderboardMemberId(member: LeaderboardSourceMember): string | null {
  const id = member.id || member.userId;
  return typeof id === 'string' && id ? id : null;
}

/**
 * Rows ranked by points, highest first; ties keep member order. Members
 * without a uid are dropped; a member missing from pointsById has 0 points.
 */
export function buildLeaderboard(
  members: readonly LeaderboardSourceMember[],
  pointsById: ReadonlyMap<string, number>,
): LeaderboardMember[] {
  const rows: LeaderboardMember[] = [];
  const seen = new Set<string>();
  for (const m of members) {
    const id = leaderboardMemberId(m);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    rows.push({
      id,
      name: m.displayName || m.name || `${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email || 'Unknown',
      nameEn: `${m.firstNameEn || m.firstName || ''} ${m.lastNameEn || m.lastName || ''}`.trim(),
      nameAr: `${m.firstNameAr || m.firstName || ''} ${m.lastNameAr || m.lastName || ''}`.trim(),
      photoURL: m.photoURL,
      rewardPoints: pointsById.get(id) || 0,
      rank: 0,
    });
  }
  return rows
    .sort((a, b) => b.rewardPoints - a.rewardPoints)
    .map((row, index) => ({ ...row, rank: index + 1 }));
}
